import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  normalizeError,
  workgroupsApi,
  type WorkGroupContentsQueryParams,
  type WorkGroupFileMetadata,
  type WorkGroupSummary,
  type WorkGroupTrashQueryParams,
  type WorkGroupFolderPermissionLevel,
} from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useWorkgroupsUIStore,
  WORKGROUPS_PRIVATE_REASON_MIN_LEN,
} from '@/stores/workgroups-ui-store';

export const workgroupsKeys = {
  all: ['workgroups'] as const,
  detail: (workGroupId: string) => ['workgroups', workGroupId] as const,
  contents: (workGroupId: string, folderId: string | undefined, includeDeleted?: boolean) =>
    ['workgroups', workGroupId, 'contents', folderId ?? 'root', includeDeleted ?? false] as const,
  meQuota: ['workgroups', 'me', 'quota'] as const,
  mePrivate: ['workgroups', 'me', 'private'] as const,
  trash: (workGroupId: string) => ['workgroups', workGroupId, 'trash'] as const,
  members: (workGroupId: string) => ['workgroups', workGroupId, 'members'] as const,
  folderPermissions: (workGroupId: string, folderId: string) =>
    ['workgroups', workGroupId, 'folder-permissions', folderId] as const,
  fileVersions: (workGroupId: string, fileId: string) =>
    ['workgroups', workGroupId, 'file-versions', fileId] as const,
  adminQuotas: ['workgroups', 'admin', 'quotas'] as const,
};

function invalidateWorkGroupContents(
  queryClient: ReturnType<typeof useQueryClient>,
  workGroupId: string,
  folderId?: string | null,
) {
  void queryClient.invalidateQueries({
    queryKey: ['workgroups', workGroupId, 'contents'],
  });
  if (folderId) {
    void queryClient.invalidateQueries({
      queryKey: workgroupsKeys.contents(workGroupId, folderId),
    });
  }
  void queryClient.invalidateQueries({
    queryKey: workgroupsKeys.contents(workGroupId, undefined),
  });
}

function invalidateWorkGroupQuota(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: workgroupsKeys.meQuota });
}

export function useWorkgroupsPrivateAccess(workGroupId: string | undefined, workGroup?: WorkGroupSummary) {
  const { user } = useAuth();
  const { hasCapability } = usePermissions();
  const storedReason = useWorkgroupsUIStore((s) =>
    workGroupId ? s.privateAccessByWorkGroupId[workGroupId] : undefined,
  );
  const setStoredReason = useWorkgroupsUIStore((s) => s.setPrivateAccessReason);
  const clearStoredReason = useWorkgroupsUIStore((s) => s.clearPrivateAccessReason);
  const [promptOpen, setPromptOpen] = useState(false);

  const canPrivateAdmin = hasCapability('workgroups.private.admin');
  const isOwner = !!workGroup?.ownerId && workGroup.ownerId === user?.id;
  const isPrivateType = workGroup?.type === 'private';
  const needsReason =
    isPrivateType && !isOwner && canPrivateAdmin && !!workGroupId;

  const privateAccessReason = useMemo(() => {
    if (!needsReason) return undefined;
    const trimmed = storedReason?.trim() ?? '';
    return trimmed.length >= WORKGROUPS_PRIVATE_REASON_MIN_LEN ? trimmed : undefined;
  }, [needsReason, storedReason]);

  const reasonMissing = needsReason && !privateAccessReason;

  return {
    needsReason,
    reasonMissing,
    privateAccessReason,
    promptOpen: promptOpen || reasonMissing,
    setPromptOpen,
    submitReason: (reason: string) => {
      if (!workGroupId) return;
      setStoredReason(workGroupId, reason);
      setPromptOpen(false);
    },
    clearReason: () => {
      if (!workGroupId) return;
      clearStoredReason(workGroupId);
      setPromptOpen(true);
    },
    canPrivateAdmin,
  };
}

export function useWorkGroups() {
  return useQuery({
    queryKey: workgroupsKeys.all,
    queryFn: async () => {
      const res = await workgroupsApi.listWorkGroups();
      return res.data;
    },
  });
}

export function useWorkGroup(
  workGroupId: string | undefined,
  privateAccessReason?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: workGroupId
      ? [...workgroupsKeys.detail(workGroupId), privateAccessReason ?? null]
      : ['workgroups', 'detail', 'idle'],
    queryFn: async () => {
      if (!workGroupId) throw new Error('Work group ID is required');
      const res = await workgroupsApi.getWorkGroup(workGroupId, privateAccessReason);
      return res.data;
    },
    enabled: !!workGroupId && enabled,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useMyPrivateWorkGroup() {
  return useQuery({
    queryKey: workgroupsKeys.mePrivate,
    queryFn: async () => {
      const res = await workgroupsApi.getMyPrivateWorkGroup();
      return res.data;
    },
  });
}

export function useMyWorkGroupQuota() {
  return useQuery({
    queryKey: workgroupsKeys.meQuota,
    queryFn: async () => {
      const res = await workgroupsApi.getMyQuota();
      return res.data;
    },
  });
}

export function useWorkGroupContents(
  workGroupId: string | undefined,
  folderId: string | undefined,
  query?: Pick<WorkGroupContentsQueryParams, 'limit' | 'cursor' | 'includeDeleted'>,
  privateAccessReason?: string,
  enabled = true,
) {
  const includeDeleted = query?.includeDeleted;
  return useQuery({
    queryKey: workGroupId
      ? [
          ...workgroupsKeys.contents(workGroupId, folderId, includeDeleted),
          privateAccessReason ?? null,
        ]
      : ['workgroups', 'contents', 'idle'],
    queryFn: async () => {
      if (!workGroupId) throw new Error('Work group ID is required');
      const res = await workgroupsApi.getContents(
        workGroupId,
        {
          folderId,
          limit: query?.limit,
          cursor: query?.cursor,
          includeDeleted: query?.includeDeleted,
        },
        privateAccessReason,
      );
      return res.data;
    },
    enabled: !!workGroupId && enabled,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useWorkGroupTrash(
  workGroupId: string | undefined,
  query?: WorkGroupTrashQueryParams,
  enabled = true,
  privateAccessReason?: string,
) {
  return useQuery({
    queryKey: workGroupId
      ? [...workgroupsKeys.trash(workGroupId), privateAccessReason ?? null]
      : ['workgroups', 'trash', 'idle'],
    queryFn: async () => {
      if (!workGroupId) throw new Error('Work group ID is required');
      const res = await workgroupsApi.listTrash(workGroupId, query, privateAccessReason);
      return res.data;
    },
    enabled: !!workGroupId && enabled,
  });
}

export function useWorkGroupFileVersions(
  workGroupId: string | undefined,
  fileId: string | undefined,
  privateAccessReason?: string,
  enabled = true,
) {
  return useQuery({
    queryKey:
      workGroupId && fileId
        ? [...workgroupsKeys.fileVersions(workGroupId, fileId), privateAccessReason ?? null]
        : ['workgroups', 'file-versions', 'idle'],
    queryFn: async () => {
      if (!workGroupId || !fileId) throw new Error('Work group and file are required');
      const res = await workgroupsApi.getFileVersions(workGroupId, fileId, privateAccessReason);
      return res.data;
    },
    enabled: !!workGroupId && !!fileId && enabled,
  });
}

export function useCreateStandaloneWorkGroup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const res = await workgroupsApi.createStandaloneWorkGroup({ name: payload.name, type: 'standalone' });
      return res.data;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.mePrivate });
      if (created?.id) {
        navigate(`/workgroups/${created.id}`);
      }
    },
  });
}

export function useGetOrCreateProjectWorkGroup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const res = await workgroupsApi.getOrCreateProjectWorkGroup(projectId);
      return res.data;
    },
    onSuccess: (workGroup) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      if (workGroup?.id) {
        navigate(`/workgroups/${workGroup.id}`);
      }
    },
  });
}

export function useUpdateWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workGroupId, name }: { workGroupId: string; name: string }) => {
      const res = await workgroupsApi.updateWorkGroup(workGroupId, { name });
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.detail(workGroupId) });
    },
  });
}

export function useArchiveWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workGroupId,
      privateAccessReason,
    }: {
      workGroupId: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.archiveWorkGroup(workGroupId, privateAccessReason);
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.detail(workGroupId) });
    },
  });
}

export function useUnarchiveWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workGroupId,
      privateAccessReason,
    }: {
      workGroupId: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.unarchiveWorkGroup(workGroupId, privateAccessReason);
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.detail(workGroupId) });
    },
  });
}

export function useDeleteWorkGroup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      workGroupId,
      privateAccessReason,
    }: {
      workGroupId: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.deleteWorkGroup(workGroupId, privateAccessReason);
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      queryClient.removeQueries({ queryKey: workgroupsKeys.detail(workGroupId) });
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.all });
      navigate('/workgroups');
    },
  });
}

export function useEmptyWorkGroupTrash() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workGroupId,
      privateAccessReason,
    }: {
      workGroupId: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.emptyTrash(workGroupId, privateAccessReason);
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(workGroupId) });
      invalidateWorkGroupContents(queryClient, workGroupId);
      invalidateWorkGroupQuota(queryClient);
    },
  });
}

export function useCreateWorkGroupFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      name: string;
      parentId?: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.createFolder(
        vars.workGroupId,
        { name: vars.name, parentId: vars.parentId },
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.parentId);
    },
  });
}

export function useUpdateWorkGroupFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      name: string;
      parentId?: string | null;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.updateFolder(
        vars.workGroupId,
        vars.folderId,
        { name: vars.name },
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
      if (vars.parentId) {
        invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.parentId);
      }
    },
  });
}

export function useDeleteWorkGroupFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      parentId?: string | null;
      privateAccessReason?: string;
    }) => {
      await workgroupsApi.deleteFolder(vars.workGroupId, vars.folderId, vars.privateAccessReason);
    },
    onSuccess: (_data, vars) => {
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.parentId ?? undefined);
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
    },
  });
}

export function useTrashWorkGroupFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      folderId?: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.trashFile(
        vars.workGroupId,
        vars.fileId,
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
    },
  });
}

export function useUploadWorkGroupFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      file: File;
      folderId?: string;
      privateAccessReason?: string;
      onUploadProgress?: (percent: number | null) => void;
    }) => {
      const res = await workgroupsApi.uploadFile(
        vars.workGroupId,
        vars.file,
        vars.folderId,
        vars.privateAccessReason,
        vars.onUploadProgress,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
      invalidateWorkGroupQuota(queryClient);
    },
  });
}

export function useDownloadWorkGroupFile() {
  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      fileName: string;
      privateAccessReason?: string;
    }) => {
      await downloadWorkGroupFileWithFallback(vars);
    },
  });
}

export function useDownloadWorkGroupFileVersion() {
  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      versionId: string;
      fileName: string;
      privateAccessReason?: string;
    }) => {
      await downloadWorkGroupFileVersionWithFallback(vars);
    },
  });
}

async function downloadWorkGroupFileWithFallback(vars: {
  workGroupId: string;
  fileId: string;
  fileName: string;
  privateAccessReason?: string;
}) {
  try {
    const { data } = await workgroupsApi.getDownloadFileAsJson(
      vars.workGroupId,
      vars.fileId,
      vars.privateAccessReason,
    );
    if (data?.url) {
      triggerBrowserDownloadFromUrl(
        data.url,
        data.fileName || vars.fileName,
        data.openMode === 'new-tab' ? 'new-tab' : 'download',
      );
      return;
    }
  } catch (e) {
    if (!axios.isAxiosError(e) || e.response?.status !== 400) {
      throw e;
    }
  }

  const response = await workgroupsApi.downloadFile(
    vars.workGroupId,
    vars.fileId,
    vars.privateAccessReason,
  );
  triggerBrowserDownloadFromBlob(response.data, vars.fileName);
}

async function downloadWorkGroupFileVersionWithFallback(vars: {
  workGroupId: string;
  fileId: string;
  versionId: string;
  fileName: string;
  privateAccessReason?: string;
}) {
  try {
    const { data } = await workgroupsApi.getDownloadFileVersionAsJson(
      vars.workGroupId,
      vars.fileId,
      vars.versionId,
      vars.privateAccessReason,
    );
    if (data?.url) {
      triggerBrowserDownloadFromUrl(
        data.url,
        data.fileName || vars.fileName,
        data.openMode === 'new-tab' ? 'new-tab' : 'download',
      );
      return;
    }
  } catch (e) {
    if (!axios.isAxiosError(e) || e.response?.status !== 400) {
      throw e;
    }
  }

  const response = await workgroupsApi.downloadFileVersion(
    vars.workGroupId,
    vars.fileId,
    vars.versionId,
    vars.privateAccessReason,
  );
  triggerBrowserDownloadFromBlob(response.data, vars.fileName);
}

function triggerBrowserDownloadFromUrl(
  url: string,
  fileName: string,
  openMode: 'new-tab' | 'download',
) {
  if (openMode === 'new-tab') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function triggerBrowserDownloadFromBlob(blobData: BlobPart, fileName: string) {
  const blob = new Blob([blobData]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function useRestoreTrashFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      folderId?: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.restoreTrashFile(
        vars.workGroupId,
        vars.fileId,
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
    },
  });
}

export function usePermanentlyDeleteTrashFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      privateAccessReason?: string;
    }) => {
      await workgroupsApi.permanentlyDeleteTrashFile(
        vars.workGroupId,
        vars.fileId,
        vars.privateAccessReason,
      );
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
      invalidateWorkGroupQuota(queryClient);
    },
  });
}

export function useRestoreTrashFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.restoreTrashFolder(
        vars.workGroupId,
        vars.folderId,
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
      invalidateWorkGroupContents(queryClient, vars.workGroupId);
    },
  });
}

export function usePermanentlyDeleteTrashFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      privateAccessReason?: string;
    }) => {
      await workgroupsApi.permanentlyDeleteTrashFolder(
        vars.workGroupId,
        vars.folderId,
        vars.privateAccessReason,
      );
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.trash(vars.workGroupId) });
    },
  });
}

export function useRestoreFileVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      versionId: string;
      folderId?: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.restoreFileVersion(
        vars.workGroupId,
        vars.fileId,
        vars.versionId,
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: workgroupsKeys.fileVersions(vars.workGroupId, vars.fileId),
      });
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
    },
  });
}

export function useUploadWorkGroupFileVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      fileId: string;
      file: File;
      folderId?: string;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.uploadNewFileVersion(
        vars.workGroupId,
        vars.fileId,
        vars.file,
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: workgroupsKeys.fileVersions(vars.workGroupId, vars.fileId),
      });
      invalidateWorkGroupContents(queryClient, vars.workGroupId, vars.folderId);
      invalidateWorkGroupQuota(queryClient);
    },
  });
}

export function useUpsertWorkGroupFolderPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      userId: string;
      level: WorkGroupFolderPermissionLevel;
      privateAccessReason?: string;
    }) => {
      const res = await workgroupsApi.upsertFolderPermission(
        vars.workGroupId,
        vars.folderId,
        { userId: vars.userId, level: vars.level },
        vars.privateAccessReason,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: workgroupsKeys.folderPermissions(vars.workGroupId, vars.folderId),
      });
    },
  });
}

export function useDeleteWorkGroupFolderPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      workGroupId: string;
      folderId: string;
      userId: string;
      privateAccessReason?: string;
    }) => {
      await workgroupsApi.deleteFolderPermission(
        vars.workGroupId,
        vars.folderId,
        vars.userId,
        vars.privateAccessReason,
      );
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: workgroupsKeys.folderPermissions(vars.workGroupId, vars.folderId),
      });
    },
  });
}

export function useWorkGroupFolderPermissions(
  workGroupId: string | undefined,
  folderId: string | undefined,
  privateAccessReason?: string,
) {
  return useQuery({
    queryKey:
      workGroupId && folderId
        ? workgroupsKeys.folderPermissions(workGroupId, folderId)
        : ['workgroups', 'folder-permissions', 'pending'],
    queryFn: async () => {
      if (!workGroupId || !folderId) throw new Error('Work group and folder are required');
      const res = await workgroupsApi.listFolderPermissions(workGroupId, folderId, privateAccessReason);
      return res.data;
    },
    enabled: !!workGroupId && !!folderId,
  });
}

export function useWorkGroupMembers(workGroupId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: workGroupId
      ? workgroupsKeys.members(workGroupId)
      : ['workgroups', 'members', 'idle'],
    queryFn: async () => {
      if (!workGroupId) throw new Error('Work group ID is required');
      const res = await workgroupsApi.listMembers(workGroupId);
      return res.data;
    },
    enabled: !!workGroupId && enabled,
  });
}

export function useAddWorkGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { workGroupId: string; userId: string }) => {
      const res = await workgroupsApi.addMember(vars.workGroupId, { userId: vars.userId });
      return res.data;
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.members(workGroupId) });
    },
  });
}

export function useRemoveWorkGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { workGroupId: string; userId: string }) => {
      await workgroupsApi.removeMember(vars.workGroupId, vars.userId);
    },
    onSuccess: (_data, { workGroupId }) => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.members(workGroupId) });
    },
  });
}

export function useAdminWorkGroupQuotas(enabled = true) {
  return useQuery({
    queryKey: workgroupsKeys.adminQuotas,
    queryFn: async () => {
      const res = await workgroupsApi.listAdminQuotas();
      return res.data;
    },
    enabled,
  });
}

export function useUpdateAdminWorkGroupQuota() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { userId: string; quotaBytes: string }) => {
      const res = await workgroupsApi.updateAdminQuota(vars.userId, { quotaBytes: vars.quotaBytes });
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workgroupsKeys.adminQuotas });
      invalidateWorkGroupQuota(queryClient);
    },
  });
}

export function workgroupsMutationErrorMessage(error: unknown): string {
  const message = normalizeError(error).message;
  return mapWorkgroupsUserFacingErrorMessage(message);
}

export type WorkGroupQuotaThreshold = 'normal' | 'warning' | 'critical' | 'exceeded';

const WORKGROUP_QUOTA_EXCEEDED_MESSAGE = 'WorkGroup storage quota exceeded';

const FOLDER_DEPTH_ERROR_PATTERNS = [
  /^Folder depth cannot exceed (\d+)$/,
  /^Folder depth exceeds maximum \((\d+)\)$/,
] as const;

const CONFLICT_COPY_STEM_PATTERN = / \(copy(?: \d+)?\)$/;

export function formatWorkGroupBytesLabel(value: string): string {
  try {
    const n = BigInt(value);
    if (n === 0n) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(n);
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
      v /= 1024;
      u += 1;
    }
    const digits = u === 0 ? 0 : v < 10 ? 2 : v < 100 ? 1 : 0;
    return `${v.toFixed(digits)} ${units[u]}`;
  } catch {
    return value;
  }
}

export function getWorkGroupQuotaPercent(quota: { usedBytes: string; quotaBytes: string }): number {
  try {
    const used = BigInt(quota.usedBytes);
    const total = BigInt(quota.quotaBytes);
    if (total <= 0n) return used > 0n ? 100 : 0;
    const scaled = (used * 10000n) / total;
    return Number(scaled) / 100;
  } catch {
    return 0;
  }
}

export function getWorkGroupQuotaThreshold(percent: number): WorkGroupQuotaThreshold {
  if (percent >= 100) return 'exceeded';
  if (percent >= 95) return 'critical';
  if (percent >= 80) return 'warning';
  return 'normal';
}

export function isWorkGroupQuotaExceededError(error: unknown): boolean {
  return normalizeError(error).message === WORKGROUP_QUOTA_EXCEEDED_MESSAGE;
}

/** Mirrors backend workgroup-files PREVIEWABLE_MIME_TYPES for row Open affordance. */
const WORKGROUP_PREVIEWABLE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

export function isWorkGroupPreviewableMimeType(mimeType?: string | null): boolean {
  if (!mimeType?.trim()) return false;
  return WORKGROUP_PREVIEWABLE_MIME_TYPES.has(mimeType.trim().toLowerCase());
}

export function isConflictCopyDisplayName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  const lastDot = trimmed.lastIndexOf('.');
  const stem = lastDot > 0 ? trimmed.slice(0, lastDot) : trimmed;
  return CONFLICT_COPY_STEM_PATTERN.test(stem);
}

export function isConflictCopyFileMetadata(file: WorkGroupFileMetadata): boolean {
  const name = file.displayName?.trim() || file.originalName;
  return isConflictCopyDisplayName(name);
}

export function getWorkGroupUploaderLabel(
  uploadedBy: { displayName: string; email: string } | null | undefined,
): string {
  const displayName = uploadedBy?.displayName?.trim();
  if (displayName) return `Uploaded by ${displayName}`;
  const email = uploadedBy?.email?.trim();
  if (email) return `Uploaded by ${email}`;
  return 'Uploaded by Unknown user';
}

function mapWorkgroupsUserFacingErrorMessage(message: string): string {
  if (message === WORKGROUP_QUOTA_EXCEEDED_MESSAGE) {
    return 'Storage quota is full. Delete files or empty trash to free space.';
  }

  for (const pattern of FOLDER_DEPTH_ERROR_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      return `Cannot add a folder here — maximum folder depth (${match[1]} levels) has been reached.`;
    }
  }

  return message;
}
